import React, { memo } from 'react';
import type { Todo } from '@ext-bookmark/shared/types';
import type { Holiday } from '../../../services/todo/holidays';
import { toggleTodoComplete, getBaseTodoId } from '../../../services/todo/storage';
import { getDayTodoType } from '../shared/utils';

interface DesktopDateCellProps {
  date: Date;
  todos: Todo[];
  isCurrentMonthDay: boolean;
  isTodayDay: boolean;
  isSelectedDay: boolean;
  holidays: Map<string, Holiday>;
  currentUserId: string | null;
  onDateClick: (date: Date) => void;
  onDateDoubleClick: (date: Date) => void;
  onTodoClick: (todo: Todo) => void;
  onTodosUpdate: () => void;
  /** 更新整月 Map 的 setState（格子内用函数式更新单格，便于父组件 memo 子组件） */
  setMonthTodos?: React.Dispatch<React.SetStateAction<Map<string, Todo[]>>>;
  /** 当前格子的日期 key（与 setMonthTodos 配合做局部更新） */
  cellDateKey?: string;
  /** 勾选成功后调用，用于使 React Query 失效（仅错误路径）；成功路径改用 onTodoUpdated 局部更新 */
  onTodosInvalidate?: () => void;
  /** 勾选成功后传入更新后的 todo，用于父组件局部更新 React Query 缓存 */
  onTodoUpdated?: (updatedTodo: Todo) => void;
  /** 勾选时立即乐观更新 React Query 缓存 */
  onOptimisticToggle?: (todoId: string, newCompleted: boolean, instanceDate?: number) => void;
  /** 已登录时传入：切换时实时获取最新 todos，避免闭包滞后 */
  getCurrentTodosForToggle?: () => Todo[] | undefined;
  /** @deprecated 使用 getCurrentTodosForToggle */
  currentTodosForToggle?: Todo[] | null;
  /** 紧凑模式：单格宽度不足时仅显示日期+底边色条 */
  isCompact?: boolean;
}

const DesktopDateCellInner: React.FC<DesktopDateCellProps> = ({
  date,
  todos,
  isCurrentMonthDay,
  isTodayDay,
  isSelectedDay,
  holidays,
  currentUserId,
  onDateClick,
  onDateDoubleClick,
  onTodoClick,
  onTodosUpdate: _onTodosUpdate,
  setMonthTodos,
  cellDateKey,
  onTodosInvalidate,
  onTodoUpdated,
  onOptimisticToggle,
  getCurrentTodosForToggle,
  currentTodosForToggle,
  isCompact = false,
}) => {
  const dateKey = date.toDateString();
  const todoType = getDayTodoType(todos, currentUserId);
  const dotColor = todoType === 'assigned' ? 'bg-amber-500 dark:bg-amber-400' : todoType === 'group' ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-primary';
  const effectiveDateKey = cellDateKey ?? dateKey;
  const dateStr = date.toISOString().split('T')[0];
  const holiday = holidays.get(dateStr);

  const handleTodoToggle = async (e: React.MouseEvent, todo: Todo) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    
    const originalTodoId = getBaseTodoId(todo);
    const isRecurrenceInstance = !!todo.recurrence && todo.id !== originalTodoId;
    const instanceDate = isRecurrenceInstance ? todo.startDate : undefined;
    
    // 先更新UI状态（乐观更新）
    const newCompleted = !todo.completed;
    const updatedTodos = todos.map(t => 
      t.id === todo.id ? { ...t, completed: newCompleted } : t
    );
    
    if (setMonthTodos) {
      setMonthTodos((prev) => {
        const next = new Map(prev);
        next.set(effectiveDateKey, updatedTodos);
        return next;
      });
    }
    onOptimisticToggle?.(originalTodoId, newCompleted, instanceDate);

    try {
      const updated = await toggleTodoComplete(originalTodoId, instanceDate, {
        getCurrentTodos: getCurrentTodosForToggle,
        currentTodos: getCurrentTodosForToggle ? undefined : (currentTodosForToggle ?? undefined),
        currentInstanceCompleted: todo.completed,
      });
      if (updated) {
        const normalizedInstance = instanceDate !== undefined
          ? new Date(instanceDate).setHours(0, 0, 0, 0)
          : undefined;
        const completed = updated.recurrence && normalizedInstance !== undefined
          ? (updated.recurrence.completedDates || []).includes(normalizedInstance)
          : !!updated.completed;
        if (setMonthTodos) {
          setMonthTodos((prev) => {
            const next = new Map(prev);
            next.set(effectiveDateKey, todos.map(t => t.id !== todo.id ? t : { ...t, completed, recurrence: updated.recurrence ?? t.recurrence }));
            return next;
          });
        }
        onTodoUpdated?.(updated);
      }
    } catch (error) {
      console.error('[DesktopDateCell] 切换待办事项状态失败:', error);
      if (setMonthTodos) {
        setMonthTodos((prev) => {
          const next = new Map(prev);
          next.set(effectiveDateKey, todos);
          return next;
        });
      }
      onTodosInvalidate?.();
    }
  };

  return (
    <div
      className={`
        p-1.5 rounded-lg flex flex-col overflow-hidden min-h-0 cursor-pointer
        transition-all duration-200 ease-out
        bg-transparent
        ${isSelectedDay ? 'ring-2 ring-primary/40 bg-primary/10 ring-inset shadow-sm' : isTodayDay && !isSelectedDay ? 'bg-primary/5 dark:bg-primary/10 shadow-sm' : ''}
        hover:bg-black/[0.04] dark:hover:bg-white/[0.06] hover:shadow-sm
      `}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') {
          return;
        }
        if (target.closest('input[type="checkbox"]')) {
          return;
        }
        if (target.closest('button')) {
          return;
        }
        onDateClick(date);
      }}
      onDoubleClick={(e) => {
        const target = e.target as HTMLElement;
        if (
          target.closest('input[type="checkbox"]') ||
          target.closest('[data-todo-text]') ||
          target.closest('[data-todo-item]')
        ) {
          return;
        }
        onDateDoubleClick(date);
      }}
      title="单击选中日期，双击添加/编辑待办事项"
    >
      <div className="flex flex-col h-full">
        {/* 日期数字和节假日标识 */}
        <div className="flex items-center justify-between mb-1 flex-shrink-0 gap-1">
          <div className={`
            text-sm font-medium flex-shrink-0
            ${!isCurrentMonthDay ? 'text-muted-foreground/70' : ''}
            ${isTodayDay && !isSelectedDay ? 'text-primary font-bold' : isCurrentMonthDay ? 'text-foreground' : ''}
            ${isSelectedDay && !isTodayDay ? 'text-primary font-semibold' : ''}
            ${isTodayDay && isSelectedDay ? 'text-primary font-bold' : ''}
          `}>
            {date.getDate()}
          </div>
          {holiday && (
            <>
              {!holiday.isWorkday || holiday.isOffDay ? (
                <span 
                  className={`text-[9px] px-1 py-0.5 rounded font-medium flex-shrink-0 truncate max-w-[50px] ${
                    holiday.isWorkday && holiday.isOffDay
                      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                      : 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
                  }`}
                  title={holiday.isWorkday && holiday.isOffDay ? `${holiday.name}（调休休息）` : holiday.name}
                >
                  {holiday.name}
                </span>
              ) : (
                <span 
                  className="text-[9px] px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 font-medium flex-shrink-0 truncate max-w-[50px]"
                  title={`${holiday.name}（调休补班）`}
                >
                  补班
                </span>
              )}
            </>
          )}
        </div>

        {/* 紧凑模式：小圆点；非紧凑：待办列表 */}
        {isCompact && todos.length > 0 && isCurrentMonthDay && (
          <div className="mt-0.5 flex justify-center">
            <div className={`w-1.5 h-1.5 rounded-full ${dotColor} shadow-sm`} />
          </div>
        )}

        {/* 待办事项列表（紧凑模式不显示） */}
        {!isCompact && (
        <div className="space-y-1 flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-subtle">
          {todos.slice(0, 3).map((todo) => {
            return (
              <div
                key={todo.id}
                data-todo-item
                className="flex items-start gap-1.5 group rounded px-1 py-0.5 -mx-1 transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700/70 active:bg-gray-200 dark:active:bg-gray-700"
                onMouseEnter={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={(e) => handleTodoToggle(e, todo)}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                  }}
                  onMouseUp={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                  }}
                  className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                    todo.completed
                      ? 'bg-ios-blue border-ios-blue shadow-sm'
                      : 'bg-transparent border-gray-300 dark:border-gray-500 hover:border-ios-blue/50'
                  }`}
                  title="点击切换完成状态"
                  aria-label={todo.completed ? '标记为未完成' : '标记为完成'}
                >
                  {todo.completed && (
                    <svg
                      className="w-2.5 h-2.5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </button>
                <div 
                  data-todo-text
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    onTodoClick(todo);
                  }}
                  className={`
                    flex-1 min-w-0 text-xs truncate cursor-pointer flex items-center gap-1
                    ${todo.completed ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}
                    transition-opacity group-hover:opacity-100
                  `}
                  style={{ color: todo.fontColor && !todo.completed ? todo.fontColor : undefined }}
                  title={todo.title}
                >
                  {/* 任务类型标识 */}
                  {(() => {
                    if (currentUserId && todo.assignedTo && todo.assignedTo.includes(currentUserId)) {
                      return <span className="text-[8px] text-orange-500 dark:text-orange-400 flex-shrink-0">●</span>;
                    }
                    if (todo.groupId) {
                      return <span className="text-[8px] text-green-500 dark:text-green-400 flex-shrink-0">●</span>;
                    }
                    return <span className="text-[8px] text-blue-500 dark:text-blue-400 flex-shrink-0">●</span>;
                  })()}
                  <span className="truncate">{todo.title}</span>
                </div>
              </div>
            );
          })}
          {todos.length > 3 && (
            <div 
              className="text-[10px] text-gray-500 dark:text-gray-400 px-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded -mx-1 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onDateClick(date);
              }}
            >
              +{todos.length - 3}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

/** 仅当本格子的 todos/日期/节假日等变化时重渲染，避免勾选一格导致整表闪 */
export const DesktopDateCell = memo(DesktopDateCellInner, (prev, next) => {
  return (
    prev.date.getTime() === next.date.getTime() &&
    prev.todos === next.todos &&
    prev.holidays === next.holidays &&
    prev.isCurrentMonthDay === next.isCurrentMonthDay &&
    prev.isTodayDay === next.isTodayDay &&
    prev.isSelectedDay === next.isSelectedDay &&
    prev.currentUserId === next.currentUserId &&
    prev.getCurrentTodosForToggle === next.getCurrentTodosForToggle &&
    prev.currentTodosForToggle === next.currentTodosForToggle &&
    prev.isCompact === next.isCompact
  );
});
